import {
  IconFile,
  IconFileText,
  IconFileTypeDoc,
  IconFileTypePdf,
  IconFileTypePpt,
  IconFileTypeXls,
  IconFileZip,
  IconMusic,
  IconPhoto,
  IconVideo,
  type Icon,
} from '@tabler/icons-react';

/** Иконка + цвет для типа файла. */
interface FileTypeVisual {
  icon: Icon;
  color: string;
}

const ARCHIVE_EXT = /\.(zip|rar|7z|tar|gz|bz2)$/i;
const WORD_EXT = /\.(docx?|rtf|odt)$/i;
const EXCEL_EXT = /\.(xlsx?|csv|ods)$/i;
const PPT_EXT = /\.(pptx?|odp)$/i;

/**
 * Тип файла → иконка/цвет. Сначала по mime_type, затем по расширению
 * (mime nullable: старые записи и нестандартные загрузки).
 */
function resolveFileTypeVisual(mimeType: string | null | undefined, name: string): FileTypeVisual {
  const mime = (mimeType ?? '').toLowerCase();

  if (mime.startsWith('image/')) return { icon: IconPhoto, color: 'var(--mantine-color-teal-6)' };
  if (mime.startsWith('video/')) return { icon: IconVideo, color: 'var(--mantine-color-grape-6)' };
  if (mime.startsWith('audio/')) return { icon: IconMusic, color: 'var(--mantine-color-indigo-6)' };
  if (mime === 'application/pdf' || /\.pdf$/i.test(name)) {
    return { icon: IconFileTypePdf, color: 'var(--mantine-color-red-6)' };
  }
  if (mime.includes('word') || WORD_EXT.test(name)) {
    return { icon: IconFileTypeDoc, color: 'var(--mantine-color-blue-6)' };
  }
  if (
    mime.includes('excel') ||
    mime.includes('spreadsheet') ||
    mime === 'text/csv' ||
    EXCEL_EXT.test(name)
  ) {
    return { icon: IconFileTypeXls, color: 'var(--mantine-color-green-7)' };
  }
  if (mime.includes('presentation') || mime.includes('powerpoint') || PPT_EXT.test(name)) {
    return { icon: IconFileTypePpt, color: 'var(--mantine-color-orange-6)' };
  }
  if (mime.includes('zip') || mime.includes('compressed') || ARCHIVE_EXT.test(name)) {
    return { icon: IconFileZip, color: 'var(--mantine-color-yellow-7)' };
  }
  if (mime.startsWith('text/')) {
    return { icon: IconFileText, color: 'var(--mantine-color-gray-6)' };
  }
  return { icon: IconFile, color: 'var(--mantine-color-gray-6)' };
}

/** Иконка типа файла для списков Диска (по mime_type с fallback на расширение). */
export function FileTypeIcon({
  mimeType,
  name,
  size = 16,
}: {
  mimeType: string | null | undefined;
  /** Имя файла — fallback-определение типа по расширению. */
  name: string;
  size?: number;
}) {
  const { icon: Icon, color } = resolveFileTypeVisual(mimeType, name);
  return <Icon size={size} color={color} style={{ flexShrink: 0 }} />;
}
